import {assert} from 'Asserts';
import {findMatchingPositionColumnIndex} from 'notebook/sample-propagation/sample-collection/SamplesSourceCollector';
import {SampleFilter, SampleFilterType, SimpleSampleFilters} from 'notebook/sample-propagation/SampleFilter';
import {
  getSchemaIdForContainersColumn,
  getSchemaIdForEntityColumn,
} from 'notebook/sample-propagation/util/getSchemaIdForColumn';
import {
  ColumnDefinition,
  CustomColumnDefinition,
  getColumnLabel,
  SchemaFieldColumnDefinition,
} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {CustomColumnDataType} from 'notebook/structured-tables/custom-columns/CustomColumnConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {Table} from 'notebook/tables/constants/TableConstants';
import {TransferTableColumnGroup} from 'notebook/transfer-tables/TransferTableConstants';
import {FieldType, RequiredLinkStorableSchema} from 'schemas/fields/SchemaFieldConstants';
import APIUtil from 'util/APIUtil';

function getDestinationDisplayName(
  table: Table,
  sampleFilter: SampleFilterType,
  columnIndices: Array<number>
): string {
  if (columnIndices.length === 2) {
    // WELLS insertion
    assert(sampleFilter === SampleFilterType.WELL, 'Inserting samples to 2 columns can only be of type WELL');
    const columnGroup = table.columnConstraints[columnIndices[0]]?.columnDefinition?.columnGroup;
    if (columnGroup == null) {
      return 'Well';
    } else {
      return columnGroup === TransferTableColumnGroup.SOURCE ? 'Source Well' : 'Destination Well';
    }
  }

  assert(columnIndices.length === 1, 'Only support getting display name from 1 or 2 columns');

  assert(table.columnConstraints[columnIndices[0]] != null, 'Cannot have null columnConstraint');
  assert(
    table.columnConstraints[columnIndices[0]]?.columnDefinition != null,
    'Cannot have null columnDefinition'
  );
  const displayLabel = getColumnLabel(table.columnConstraints[columnIndices[0]]?.columnDefinition!);
  assert(displayLabel !== '', 'Cannot have empty string for destinationDisplayName');
  return displayLabel;
}

export interface AcceptableSampleFiltersAndColumns {
  /**
   * List of SampleFilters that the destination column can accept.
   */
  sampleFilters: Array<SampleFilter>;
  /**
   * Indices of the destination column in the destination table.
   * Usually of length one, except for Wells with positions, when it is length two.
   */
  columnIndices: [number] | [number, number];
  /**
   * Column label of the destination column.
   */
  destinationDisplayName: string;
}

/**
 * Bioentities can be placed into three types of columns:
 *  * EXISTING_BIOENTITY (i.e., the sample in a registration table), or
 *  * BIOENTITY_LINK schema field (i.e., any schema field that takes entities)
 *  * ANTIBODY_DOMAIN (i.e., a column for inputting antibody domains)
 */
function isBioentityColumn(columnDefinition: ColumnDefinition): boolean {
  return (
    columnDefinition.type === TableColumnType.EXISTING_BIOENTITY ||
    (columnDefinition.type === TableColumnType.TAG_VALUE &&
      columnDefinition.tagSchemaFieldType === FieldType.BIOENTITY_LINK) ||
    columnDefinition.type === TableColumnType.ANTIBODY_DOMAIN
  );
}

/**
 * Returns an array of SampleFilters and destination columns, one for each column(s) that can
 * receive samples. The filter indicates what type of samples the specified column(s) can get.
 *
 * Must be kept in sync with `makeSampleCell` and `makeSampleCells`, which create cells that will go into the
 * destination column.
 */
export default function getAcceptableSampleFiltersByColumn(
  table: Table
): Array<AcceptableSampleFiltersAndColumns> {
  const sampleTypesByColumn: Array<AcceptableSampleFiltersAndColumns> = [];
  const columnConstraints = table.columnConstraints;
  for (const [columnIndex, columnConstraint] of columnConstraints.entries()) {
    assert(
      columnConstraint != null && columnConstraint.columnDefinition != null,
      'Structured table must have a usable column spec',
      {columnIndex, columnConstraints}
    );
    const columnDefinition = columnConstraint.columnDefinition;

    // Did you really think you could add samples to a locked column.
    if (columnDefinition.locked) {
      continue;
    }

    if (isBioentityColumn(columnDefinition)) {
      sampleTypesByColumn.push({
        sampleFilters: [
          {
            type: SampleFilterType.BIOENTITY,
            schemaId: getSchemaIdForEntityColumn(columnDefinition),
          },
        ],
        columnIndices: [columnIndex],
        destinationDisplayName: getDestinationDisplayName(table, SampleFilterType.BIOENTITY, [columnIndex]),
      });
    }

    if (
      columnDefinition.type === TableColumnType.TAG_VALUE &&
      columnDefinition.tagSchemaFieldType === FieldType.STORABLE_LINK
    ) {
      const requiredLink = columnDefinition.schemaField.requiredLink as RequiredLinkStorableSchema;
      const schemaId = requiredLink && requiredLink.storableSchema ? requiredLink.storableSchema.id : null;
      if (schemaId == null) {
        sampleTypesByColumn.push({
          sampleFilters: [SimpleSampleFilters.CONTAINER, SimpleSampleFilters.BOX, SimpleSampleFilters.PLATE],
          columnIndices: [columnIndex],
          destinationDisplayName: getDestinationDisplayName(table, SampleFilterType.CONTAINER, [columnIndex]),
        });
      } else if (APIUtil.isBoxSchema(schemaId)) {
        sampleTypesByColumn.push({
          sampleFilters: [
            {
              type: SampleFilterType.BOX,
              schemaId,
            },
          ],
          columnIndices: [columnIndex],
          destinationDisplayName: getDestinationDisplayName(table, SampleFilterType.BOX, [columnIndex]),
        });
      } else if (APIUtil.isContainerSchema(schemaId)) {
        sampleTypesByColumn.push({
          sampleFilters: [
            {
              type: SampleFilterType.CONTAINER,
              schemaId,
            },
          ],
          columnIndices: [columnIndex],
          destinationDisplayName: getDestinationDisplayName(table, SampleFilterType.CONTAINER, [columnIndex]),
        });
      } else if (APIUtil.isPlateSchema(schemaId)) {
        sampleTypesByColumn.push({
          sampleFilters: [
            {
              type: SampleFilterType.PLATE,
              schemaId,
            },
          ],
          columnIndices: [columnIndex],
          destinationDisplayName: getDestinationDisplayName(table, SampleFilterType.PLATE, [columnIndex]),
        });
      }
    }

    if (
      isTagValueColumnOfType(columnDefinition, FieldType.TEXT) ||
      isCustomColumnOfType(columnDefinition, CustomColumnDataType.TEXT)
    ) {
      sampleTypesByColumn.push({
        sampleFilters: [
          {
            type: SampleFilterType.STRING,
            schemaId: getSchemaIdForEntityColumn(columnDefinition),
          },
        ],
        columnIndices: [columnIndex],
        destinationDisplayName: getDestinationDisplayName(table, SampleFilterType.STRING, [columnIndex]),
      });
    }

    if (
      isTagValueColumnOfType(columnDefinition, FieldType.BOOLEAN) ||
      isCustomColumnOfType(columnDefinition, CustomColumnDataType.BOOLEAN)
    ) {
      sampleTypesByColumn.push({
        sampleFilters: [
          {
            type: SampleFilterType.BOOLEAN,
            schemaId: getSchemaIdForEntityColumn(columnDefinition),
          },
        ],
        columnIndices: [columnIndex],
        destinationDisplayName: getDestinationDisplayName(table, SampleFilterType.BOOLEAN, [columnIndex]),
      });
    }

    if (
      isTagValueColumnOfType(columnDefinition, FieldType.FLOAT) ||
      isCustomColumnOfType(columnDefinition, CustomColumnDataType.FLOAT)
    ) {
      sampleTypesByColumn.push({
        sampleFilters: [
          {
            type: SampleFilterType.FLOAT,
            schemaId: getSchemaIdForEntityColumn(columnDefinition),
          },
          {
            type: SampleFilterType.INTEGER,
            schemaId: getSchemaIdForEntityColumn(columnDefinition),
          },
        ],
        columnIndices: [columnIndex],
        destinationDisplayName: getDestinationDisplayName(table, SampleFilterType.FLOAT, [columnIndex]),
      });
    }

    if (
      isTagValueColumnOfType(columnDefinition, FieldType.INTEGER) ||
      isCustomColumnOfType(columnDefinition, CustomColumnDataType.INTEGER)
    ) {
      sampleTypesByColumn.push({
        sampleFilters: [
          {
            type: SampleFilterType.INTEGER,
            schemaId: getSchemaIdForEntityColumn(columnDefinition),
          },
        ],
        columnIndices: [columnIndex],
        destinationDisplayName: getDestinationDisplayName(table, SampleFilterType.INTEGER, [columnIndex]),
      });
    }

    if (columnDefinition.type === TableColumnType.BOX) {
      sampleTypesByColumn.push({
        sampleFilters: [SimpleSampleFilters.BOX],
        columnIndices: [columnIndex],
        destinationDisplayName: getDestinationDisplayName(table, SampleFilterType.BOX, [columnIndex]),
      });
    }

    if (columnDefinition.type === TableColumnType.CONTAINERS) {
      sampleTypesByColumn.push({
        sampleFilters: [
          {
            type: SampleFilterType.CONTAINER,
            schemaId: getSchemaIdForContainersColumn(columnDefinition),
          },
        ],
        columnIndices: [columnIndex],
        destinationDisplayName: getDestinationDisplayName(table, SampleFilterType.CONTAINER, [columnIndex]),
      });
    }

    if (columnDefinition.type === TableColumnType.PLATE) {
      sampleTypesByColumn.push({
        sampleFilters: [SimpleSampleFilters.PLATE],
        columnIndices: [columnIndex],
        destinationDisplayName: getDestinationDisplayName(table, SampleFilterType.PLATE, [columnIndex]),
      });

      // If we have a corresponding POSITION column, we can add wells
      const positionColumnIndex = findMatchingPositionColumnIndex(table, columnDefinition.columnGroup);
      if (positionColumnIndex >= 0) {
        sampleTypesByColumn.push({
          sampleFilters: [SimpleSampleFilters.WELL],
          // We ensure that plate index precedes column index, but it's not strictly necessary
          columnIndices: [columnIndex, positionColumnIndex],
          destinationDisplayName: getDestinationDisplayName(table, SampleFilterType.WELL, [
            columnIndex,
            positionColumnIndex,
          ]),
        });
      }
    }
  }

  return sampleTypesByColumn;
}

function isTagValueColumnOfType(
  columnDefinition: ColumnDefinition,
  fieldType: FieldType
): columnDefinition is SchemaFieldColumnDefinition {
  return (
    columnDefinition.type === TableColumnType.TAG_VALUE && columnDefinition.tagSchemaFieldType === fieldType
  );
}

function isCustomColumnOfType(
  columnDefinition: ColumnDefinition,
  dataType: CustomColumnDataType
): columnDefinition is CustomColumnDefinition {
  return (
    columnDefinition.type === TableColumnType.CUSTOM &&
    columnDefinition.customColumnConfig.dataType === dataType
  );
}
